/*
 * BIS/Notify drawer components
 * <bis-opener>, <bis-drawer>
 * Note: BisDrawer extends GlobalDrawer, so this must run after global.js
 */

class BisOpener extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      const drawer = document.querySelector('#global-bis-drawer');
      if (drawer) drawer.open(button);
    });
  }
}
customElements.define('bis-opener', BisOpener);

class BisDrawer extends GlobalDrawer {
  constructor() {
    super();
    this.productData = null;
    this.form = this.querySelector('form');
    this.sizeSelect = this.querySelector('#GlobalBisFormSize');
    this.emailInput = this.querySelector('#GlobalBisFormEmail');
    this.pillsContainer = this.querySelector('[data-bis-pills]');
    this.pillTemplate = this.querySelector('[data-bis-pill-template]');
    this.submitButton = this.form?.querySelector('button');
    this.feedback = this.querySelector('[data-bis-feedback]');
    this.feedbackText = this.feedback?.querySelector('[data-bis-feedback-text]');
    this.cookieNotice = this.querySelector('[data-third-party-cookie-notice]');

    this.form?.addEventListener('submit', this.handleSubmit.bind(this));
    this.sizeSelect?.addEventListener('change', () => this.setActivePill(this.sizeSelect.value));
    this.pillsContainer?.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-bis-size]');
      if (pill) this.selectSize(pill);
    });
    this.emailInput?.addEventListener('input', () => this.updateSubmitState());
    this.updateSubmitState(); // handles prefilled customer.email on load
  }

  // visually disable the button only (allows validation messages)
  updateSubmitState() {
    const hasEmail = (this.emailInput?.value || '').trim() !== '';
    this.submitButton?.classList.toggle('disabled', !hasEmail);
  }

  // toggle the submit button spinner/load state during subscription
  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.setAttribute('aria-disabled', isLoading);
    this.submitButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
  }

  open(triggeredBy) {
    if (!this.setProductData(triggeredBy)) {
      return;
    }
    this.populateSizes();
    this.preselectSize(triggeredBy);
    this.preselectOnlyOption();
    this.resetFeedback();
    this.updateSubmitState(); // enable submit for autofill
    super.open(triggeredBy);
  }

  // when menu contains one option, pre-select it
  preselectOnlyOption() {
    if (!this.sizeSelect || this.sizeSelect.selectedIndex > 0) return;
    const selectable = Array.from(this.sizeSelect.options).filter((opt) => !opt.disabled && opt.value);
    if (selectable.length !== 1) return;

    this.sizeSelect.value = selectable[0].value;
    this.setActivePill(selectable[0].value);
  }

  // pre-select any variant associated with trigger button
  // either button[data-variant-id] (e.g. cards) or button in product form with [name="id"] (e.g. PDP)
  preselectSize(trigger) {
    const variantId =
      trigger?.dataset?.variantId ||
      trigger?.closest('form')?.querySelector('input[name="id"]')?.value;
    if (!variantId || !this.sizeSelect) return;

    const option = Array.from(this.sizeSelect.options).find(
      (opt) => opt.value === String(variantId) && !opt.disabled
    );
    if (!option) return;

    this.sizeSelect.value = variantId;
    this.setActivePill(variantId);
  }

  // reset form to submittable state
  resetFeedback() {
    if (this.form) this.form.hidden = false;
    if (this.feedback) {
      this.feedback.hidden = true;
      if (this.feedbackText) this.feedbackText.textContent = '';
      this.feedback.classList.remove('bis-form__message--error');
    }
    if (this.cookieNotice) this.cookieNotice.hidden = true;
  }

  populateSizes() {
    const variants = this.productData?.variants || [];

    if (this.sizeSelect) {
      this.sizeSelect.length = 1; // keep placeholder <option>
      this.sizeSelect.selectedIndex = 0;
    }
    if (this.pillsContainer) {
      this.pillsContainer.replaceChildren();
    }

    variants.forEach((variant) => {
      const notifiable = variant.available === false;

      if (this.sizeSelect) {
        const option = new Option(variant.title, variant.id);
        option.disabled = !notifiable;
        this.sizeSelect.add(option);
      }

      if (this.pillsContainer && this.pillTemplate) {
        const pill = this.pillTemplate.content.firstElementChild.cloneNode(true);
        pill.textContent = variant.title;
        pill.dataset.variantId = variant.id;
        pill.disabled = !notifiable;
        this.pillsContainer.appendChild(pill);
      }
    });
  }

  // Retrieve product data from [bis-opener-json]
  setProductData(trigger) {
    this.productData = null;

    // the modal is moved out of the DOM scope, so find the JSON from the trigger's scope
    const scope = trigger?.closest('product-card, .quick-add__popup, [data-bis-scope]');
    const jsonEl = scope?.querySelector('[bis-opener-json]');
    if (!jsonEl) {
      console.error('bis-drawer: no [bis-opener-json] found for trigger', trigger);
      return false;
    }

    try {
      const product = JSON.parse(jsonEl.textContent);
      // normalise URL to https:// for Swym
      let image = typeof product.image === 'string' ? product.image : '';
      if (image.startsWith('//')) image = 'https:' + image;
      this.productData = {
        productId: product.id, // Swym "empi"
        productUrl: product.url, // Swym "du"
        imageUrl: image, // Swym "iu"
        variants: (product.variants || []).map((variant) => ({
          id: variant.id, // Swym "epi"
          title: variant.title,
          available: variant.available,
        })),
      };
      return true;
    } catch (error) {
      console.error('bis-drawer: failed to parse [bis-opener-json]', error);
      return false;
    }
  }

  selectSize(pill) {
    if (this.sizeSelect) this.sizeSelect.value = pill.dataset.variantId;
    this.setActivePill(pill.dataset.variantId);
  }

  setActivePill(variantId) {
    this.pillsContainer
      ?.querySelectorAll('[data-bis-size]')
      .forEach((pill) => pill.classList.toggle('is-active', pill.dataset.variantId === variantId));
  }

  handleSubmit(event) {
    event.preventDefault();
    if (!this.productData) return;

    // <form-validation> handles error messages
    if (this.form && !this.form.checkValidity()) return;

    const variantId = this.sizeSelect.value;
    const productUrlAbs = window.location.origin + this.productData.productUrl;

    const product = {
      epi: Number(variantId),
      empi: String(this.productData.productId),
      du: productUrlAbs,
      iu: this.productData.imageUrl || '',
      ru: window.location.href, // "Referring URL" include, or Swym fills with last viewed PDP
    };

    console.debug('BIS', JSON.stringify(product));

    this.subscribe(product, this.emailInput.value);

    // Swym may mutate the product object to add things you didn't put there
//     console.debug('Swym product', product);
  }

  // Subscribe to Swym "back in stock" alert. Mirrors handling in variant-selects.js
  subscribe(product, email) {
    const onSuccess = (response) => {
      console.debug('bis-drawer: subscribed', response);
      this.setLoading(false);
      if (this.cookieNotice) this.cookieNotice.hidden = true;
      if (this.form) this.form.hidden = true; // replace the form with the success message
      this.showFeedback(this.feedback?.dataset.successMessage, false);
    };
    const onError = (error) => {
      console.error('bis-drawer: subscribe failed', error);
      this.setLoading(false);
      if (this.cookieNotice) this.cookieNotice.hidden = true;
      this.showFeedback(this.feedback?.dataset.errorMessage, true);
    };

    const swat = window._swat;
    const swatUnavailable = !swat || typeof swat.subscribeForProductAlert !== 'function';
    // Consent read via Pandectes helper (see assets/pandectes-consent.js)
    // if helper missing we don't block (preserves previous behaviour)
    const preferencesCookiesDenied = window.themeConsent
      ? !window.themeConsent.functionalityGranted()
      : false;

    // no Swym when consent denied. show cookie notice over generic error
    if (swatUnavailable && preferencesCookiesDenied) {
      if (this.cookieNotice) {
        this.cookieNotice.hidden = false;
      } else {
        onError(new Error('Back in stock signup needs third-party cookie consent.'));
      }
      return;
    }
    if (swatUnavailable) {
      onError(new Error('Back in stock signup is unavailable (Swym could not be loaded).'));
      return;
    }

    try {
      this.setLoading(true);
      swat.subscribeForProductAlert(email, 'email', product, onSuccess, onError, 0, 'backinstock');
    } catch (error) {
      onError(error);
    }
  }

  showFeedback(message, isError) {
    if (!this.feedback) return;
    if (this.feedbackText) this.feedbackText.textContent = message || '';
    this.feedback.classList.toggle('bis-form__message--error', !!isError);
    this.feedback.hidden = !message;
  }
}

customElements.define('bis-drawer', BisDrawer);
